import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import { handleRequest } from '../quipli-service-client';
import { OrderParsers } from './order-parsers';
import OrderJson = OrderParsers.OrderJson;
import {
  CanOnRentValidationError,
  CycleBillingTerms,
  InventoryReturnRequest,
  OffRentRequest,
  OnRentRequest,
  Order,
  OrderMedia,
  OrderMediaType,
  OrderReturnStatus,
  OrderStatus,
  OrderStatusTransitionPaymentRequirement,
  OrderStatusTransitionPaymentRequirementRequest,
  OrderWorkFlowStatus,
  SplitOrderProductsUpdateRequest,
  SeisCertificate,
  UpdateOrderFinancialRequest,
  OrderSearchRequest,
  CanOffRentValidationError,
  CanInventoryReturnValidationError,
} from './order';
import { ImageSize } from '../image/image';
import { DateTime } from 'luxon';
import { Pagination } from '../pagination/pagination';
import { PaginationParsers } from '../pagination/pagination-parsers';

export const useOrderApiV1 = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function searchOrders(request: OrderSearchRequest): Promise<Pagination<Order>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    const { items, pagination } = await handleRequest<PaginationParsers.PaginationJson<OrderParsers.OrderJson>>(config);
    return {
      pagination,
      items: items.map(OrderParsers.toOrder),
    };
  }

  async function approveOrder(orderId: number): Promise<Order> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/approve`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const order = await handleRequest<OrderJson>(config);
    return OrderParsers.toOrder(order);
  }

  async function canApproveOrder(orderId: number): Promise<{ isValid: boolean; errors: string[] }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/canApprove`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest<{ isValid: boolean; errors: string[] }>(config);
  }

  async function onRentOrder(orderId: number, request: OnRentRequest): Promise<Order> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/onRent`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    const order = await handleRequest<OrderJson>(config);
    return OrderParsers.toOrder(order);
  }

  async function canOnRentOrder(orderId: number): Promise<CanOnRentValidationError[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/canOnRent`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest<CanOnRentValidationError[]>(config);
  }

  async function canOffRentOrder(orderId: number, request: OffRentRequest): Promise<CanOffRentValidationError[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/canOffRent`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    return handleRequest<CanOffRentValidationError[]>(config);
  }

  async function canInventoryReturnOrder(
    orderId: number,
    request: InventoryReturnRequest,
  ): Promise<CanInventoryReturnValidationError[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/canInventoryReturn`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    return handleRequest<CanInventoryReturnValidationError[]>(config);
  }

  async function offRentOrder(orderId: number, request: OffRentRequest): Promise<Order> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/offRent`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    const order = await handleRequest<OrderJson>(config);
    return OrderParsers.toOrder(order);
  }

  async function inventoryReturn(orderId: number, request: InventoryReturnRequest): Promise<Order> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/inventoryReturn`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    const order = await handleRequest<OrderJson>(config);
    return OrderParsers.toOrder(order);
  }

  async function canEditOrder(orderId: number): Promise<{ isValid: boolean; errors: string[] }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/canEdit`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest<{ isValid: boolean; errors: string[] }>(config);
  }

  async function getOrderById(orderId: number): Promise<Order> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const o = await handleRequest<OrderParsers.OrderJson>(config);
    return OrderParsers.toOrder(o);
  }

  async function getStatusTransitionPaymentRequirement(
    status: OrderStatus | OrderReturnStatus,
  ): Promise<OrderStatusTransitionPaymentRequirement | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/orders/statusTransitionPaymentRequirement/${status}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest<OrderStatusTransitionPaymentRequirement | undefined>(config);
  }

  async function upsertOrderStatusTransitionPaymentRequirement(
    status: OrderStatusTransitionPaymentRequirementRequest,
  ): Promise<OrderStatusTransitionPaymentRequirement | undefined> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/statusTransitionPaymentRequirement`,
      headers: {
        'X-Tenant': tenant,
      },
      data: status,
    };

    return handleRequest<OrderStatusTransitionPaymentRequirement | undefined>(config);
  }

  async function getOrderMessage(orderId: number): Promise<string | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/orderMessage`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest<string | undefined>(config);
  }

  async function updateOrder(orderId: number, request: UpdateOrderFinancialRequest) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    const o = await handleRequest<OrderParsers.OrderJson>(config);
    return OrderParsers.toOrder(o);
  }

  async function updateCustomContractInvoiceText(
    orderId: number,
    defaultInvoiceFreeText: string,
    rentalDisclaimer: string,
  ) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/customContractInvoiceText`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { defaultInvoiceFreeText, rentalDisclaimer },
    };

    return handleRequest(config);
  }

  async function getUploadOrderMediaUrl(
    orderId: number,
    filename: string,
    type: OrderMediaType,
  ): Promise<{ url: string; mediaId: string }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/media/${filename}/upload`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        type,
      },
    };
    return await handleRequest<{ url: string; mediaId: string }>(config);
  }

  async function addOrderMedia(
    orderId: number,
    mediaId: string,
    filename: string,
    type: OrderMediaType,
  ): Promise<OrderMedia> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/media`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        mediaId,
        filename,
        type,
      },
    };
    return await handleRequest<OrderMedia>(config);
  }

  async function listOrderMedia(orderId: number) {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/media`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const om = await handleRequest<OrderParsers.OrderMediaJson[]>(config);
    return om.map(OrderParsers.toOrderMedia);
  }

  async function deleteOrderMedia(orderId: number, mediaId: string) {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/media/${mediaId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<number>(config);
  }

  async function getDownloadOrderMediaUrl(orderId: number, mediaId: string, forceDownload: boolean, size: ImageSize) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/media/${mediaId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        forceDownload,
        size,
      },
    };

    return await handleRequest<string>(config);
  }

  async function regenerateInvoiceForCycle(orderId: number, cycle: number) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/regenerateInvoiceForCycle?cycle=${cycle}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const orderJson = await handleRequest<OrderJson>(config);
    return OrderParsers.toOrder(orderJson);
  }

  async function cancelOrder(orderId: number, reasonId: number, comments: string, doRefund: boolean = false) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/cancel`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        reasonId,
        comments,
        doRefund,
      },
    };
    return await handleRequest<OrderJson>(config);
  }

  async function autoApprove(orderId: number) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/autoApprove`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const orderJson = await handleRequest<OrderJson>(config);
    return OrderParsers.toOrder(orderJson);
  }

  async function reopenOrder(orderId: number, status: OrderWorkFlowStatus) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/reopen`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        status,
      },
    };
    const result = await handleRequest<OrderJson>(config);
    return OrderParsers.toOrder(result);
  }

  async function deleteOrder(orderId: number) {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest(config);
  }

  async function convertContractToCycleBilling(orderId: number, cycleBillingTerms: CycleBillingTerms) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/convertToCycleBilling`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        cycleBillingTerms,
      },
    };

    const result = await handleRequest<OrderJson>(config);
    return OrderParsers.toOrder(result);
  }

  async function updateOrderCycleBillingTerms(
    orderId: number,
    cycleBillingTerms: Pick<CycleBillingTerms, 'paymentPeriod'>,
  ) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/cycleBillingTerms`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        cycleBillingTerms,
      },
    };

    return await handleRequest(config);
  }

  async function updateOrderNumberFormat(pattern: string): Promise<string> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/updateNumberFormat`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        pattern,
      },
    };

    return handleRequest<string>(config);
  }

  async function updateOrderSalesRep(orderId: number, salesRepId?: number): Promise<string> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/salesRep`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        salesRepId,
      },
    };

    return handleRequest<string>(config);
  }

  async function getSeisDamageWaiversByOrderId(orderId: number): Promise<Record<string, SeisCertificate[]>> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/seis-integration/certificates?orderId=${orderId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest<Record<string, SeisCertificate[]>>(config);
  }

  async function exportOrders(orderIds: number[]): Promise<BlobPart> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/export`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        orderIds,
      },
    };

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  async function checkUserHadAgreedToTerms(orderId: number): Promise<boolean> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/checkUserHasAgreedToTerms`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest<boolean>(config);
  }

  async function generateAcceptRentalTermsLink(orderId: number): Promise<string> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/generateAcceptRentalTermsLink`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest<string>(config);
  }

  async function createSecurityDeposit(orderId: number): Promise<Order> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/securityDeposit`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { orderId },
    };
    return await handleRequest<Order>(config);
  }

  async function markAsMissedOrder(orderId: number, comments: string, doRefund: boolean = false) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/markAsMissed`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        comments,
        doRefund,
      },
    };
    return await handleRequest<OrderJson>(config);
  }

  async function exportMissedOrders(): Promise<BlobPart> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/orders/exportMissed`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  async function splitOrderProductsUpdate(orderId: number, request: SplitOrderProductsUpdateRequest) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/splitOrderProductsUpdate`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    const o = await handleRequest<OrderParsers.OrderJson>(config);
    return OrderParsers.toOrder(o);
  }

  async function updateRentalDates(orderId: number, startDate: DateTime, endDate: DateTime): Promise<Order> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/updateRentalDates`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        startDate,
        endDate,
      },
    };

    return handleRequest<Order>(config);
  }

  function updateAutoPay(orderId: number, isAutoPay: boolean) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/orders/${orderId}/updateAutoPay`,
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
      },
      data: { isAutoPay },
    };
    return handleRequest<{ status: number; msg: string }>(config);
  }

  return {
    searchOrders,
    getOrderById,
    approveOrder,
    canApproveOrder,
    onRentOrder,
    canOnRentOrder,
    canOffRentOrder,
    canInventoryReturnOrder,
    offRentOrder,
    inventoryReturn,
    getStatusTransitionPaymentRequirement,
    upsertOrderStatusTransitionPaymentRequirement,
    getOrderMessage,
    canEditOrder,
    updateOrder,
    updateCustomContractInvoiceText,
    getUploadOrderMediaUrl,
    addOrderMedia,
    listOrderMedia,
    deleteOrderMedia,
    getDownloadOrderMediaUrl,
    regenerateInvoiceForCycle,
    cancelOrder,
    autoApprove,
    reopenOrder,
    deleteOrder,
    convertContractToCycleBilling,
    updateOrderCycleBillingTerms,
    updateOrderNumberFormat,
    updateOrderSalesRep,
    getSeisDamageWaiversByOrderId,
    exportOrders,
    checkUserHadAgreedToTerms,
    generateAcceptRentalTermsLink,
    createSecurityDeposit,
    markAsMissedOrder,
    exportMissedOrders,
    splitOrderProductsUpdate,
    updateRentalDates,
    updateAutoPay,
  };
};
